import { useState } from 'react'
import { Link } from 'react-router-dom'
import { curatedFeedback } from '../data/feedback'
import { findGame, games } from '../data/games'
import { readJson, writeJson } from '../lib/storage'
import { useSeo } from '../lib/seo'
import { useI18n } from '../i18n'

type Message = { id: string; text: string; created: number; gameId?: string }

const dateTime = (value: number | string, lang: 'en' | 'zh') => new Intl.DateTimeFormat(
  lang === 'zh' ? 'zh-CN' : 'en-US',
  { timeZone: 'Asia/Shanghai', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: lang === 'en' },
).format(new Date(value))

export default function Board() {
  const { lang } = useI18n()
  useSeo(
    'Game Feedback & Playtest Ideas | PlayFree',
    'Browse editorial playtest ideas for PlayFree games and keep your own game-specific notes in this browser.',
    '/board',
    lang,
    undefined,
    { noindex: true },
  )
  const [list,setList]=useState(()=>readJson<Message[]>('messages',[]))
  const [text,setText]=useState('')
  const [gameId,setGameId]=useState(games[0].id)
  const submit=(event:React.FormEvent)=>{
    event.preventDefault()
    if(!text.trim()) return
    const next=[{id:crypto.randomUUID(),text:text.trim(),created:Date.now(),gameId},...list]
    setList(next)
    writeJson('messages',next)
    setText('')
  }
  const remove=(id:string)=>{
    const next=list.filter(message=>message.id!==id)
    setList(next)
    writeJson('messages',next)
  }
  const gameLink=(targetId?:string)=>{
    const game=findGame(targetId)
    return game?<Link className="msg-chip game" to={`/game/${game.id}`}>{game.icon} {game.title[lang]}</Link>:null
  }
  return <section>
    <div className="board-heading">
      <div><span className="board-kicker">{lang==='zh'?'PLAYTEST NOTEBOOK':'PLAYTEST NOTEBOOK'}</span><h1>💬 {lang==='zh'?'游戏反馈与改进想法':'Game feedback & ideas'}</h1></div>
      <span className="board-count">{curatedFeedback.length + list.length} {lang==='zh'?'条记录':'notes'}</span>
    </div>
    <p className="note">{lang==='zh'?'下方内容是编辑整理的体验测试示例，不代表真实用户评价。你也可以选择具体游戏并保存自己的建议；本机留言只保存在当前浏览器，不会自动提交。真正反馈请发送至 mikefeng2013@gmail.com。':'The playtest excerpts below are editorial examples, not user testimonials. Choose a game to save your own note in this browser; local notes are never submitted automatically. To send real feedback, email mikefeng2013@gmail.com.'}</p>
    <form className="board-form" onSubmit={submit}>
      <h2>{lang==='zh'?'记录一条游戏建议':'Save a game note'}</h2>
      <label className="field"><span>{lang==='zh'?'针对游戏':'Game'}</span><select value={gameId} onChange={event=>setGameId(event.target.value)}>{games.map(game=><option value={game.id} key={game.id}>{game.icon} {game.title[lang]}</option>)}</select></label>
      <label className="field"><span>{lang==='zh'?'建议、Bug 或想法':'Suggestion, bug or idea'} <em className="counter">{text.length}/500</em></span><textarea value={text} maxLength={500} rows={4} onChange={event=>setText(event.target.value)} placeholder={lang==='zh'?'例如：在哪个操作或关卡遇到问题？你希望怎样改进？':'For example: what happened, where did it happen, and what would improve it?'}/></label>
      <button className="btn btn-primary" type="submit">{lang==='zh'?'保存在本机':'Save locally'}</button>
    </form>
    <section className="board-list" aria-labelledby="playtest-notes-title">
      <div className="board-list-head"><div><h2 id="playtest-notes-title">{lang==='zh'?'体验反馈摘录':'Playtest excerpts'}</h2><p>{lang==='zh'?'按时间倒序，覆盖具体游戏与可执行建议。':'Reverse chronological notes with a specific game and actionable suggestion.'}</p></div><span className="msg-chip sample">{lang==='zh'?'编辑示例':'Editorial examples'}</span></div>
      <ul>
        {list.map(message=><li className="msg local-note" key={message.id}><div className="msg-top"><span className="msg-avatar">✍️</span><strong>{lang==='zh'?'我的本机留言':'My local note'}</strong>{gameLink(message.gameId)}<time className="msg-time" dateTime={new Date(message.created).toISOString()}>{dateTime(message.created,lang)}</time><button className="msg-del" type="button" onClick={()=>remove(message.id)} aria-label={lang==='zh'?'删除本机留言':'Delete local note'}>{lang==='zh'?'删除':'Delete'}</button></div><p className="msg-text">{message.text}</p></li>)}
        {curatedFeedback.map(message=><li className="msg sample-note" key={message.id}><div className="msg-top"><span className="msg-avatar">{message.avatar}</span><strong>{message.author}</strong>{gameLink(message.gameId)}<span className="msg-chip">{message.topic[lang]}</span><time className="msg-time" dateTime={message.created}>{dateTime(message.created,lang)}</time></div><p className="msg-text">{message.text[lang]}</p><span className="sample-source">{lang==='zh'?'编辑整理的体验测试示例':'Editorial playtest example'}</span></li>)}
      </ul>
    </section>
  </section>
}
